'use client';

import { useEffect, useState } from 'react';
import { analytics, AnalyticsMetrics } from '@/lib/analytics';
import Icon from '@/components/ui/AppIcon';
import MetricsCard from './MetricsCard';
import ChartCard from './ChartCard';
import EventsTable from './EventsTable';

export default function AnalyticsDashboard() {
  const [metrics, setMetrics] = useState<AnalyticsMetrics | null>(null);
  const [timeRange, setTimeRange] = useState(7);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadMetrics();
  }, [timeRange]);

  const loadMetrics = () => {
    setLoading(true);
    setTimeout(() => {
      const data = analytics.getMetrics(timeRange);
      setMetrics(data);
      setLoading(false);
    }, 500);
  };

  const handleClearData = () => {
    if (confirm('Are you sure you want to clear all analytics data? This cannot be undone.')) {
      analytics.clearData();
      loadMetrics();
    }
  };

  const formatDuration = (ms: number): string => {
    const minutes = Math.floor(ms / 60000);
    const seconds = Math.floor((ms % 60000) / 1000);
    return `${minutes}m ${seconds}s`;
  };

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="bg-muted rounded-xl p-12 text-center">
          <Icon name="ArrowPathIcon" size={48} className="text-muted-foreground mx-auto mb-4 animate-spin" />
          <p className="text-muted-foreground">Loading analytics data...</p>
        </div>
      </div>
    );
  }

  if (!metrics) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="bg-card rounded-xl p-12 text-center border border-border">
          <Icon name="ChartBarIcon" size={48} className="text-muted-foreground mx-auto mb-4" />
          <p className="text-muted-foreground">No analytics data available</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      {/* Controls */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-8">
        <div className="flex items-center gap-2">
          <label className="text-sm font-medium text-foreground">Time Range:</label>
          <select
            value={timeRange}
            onChange={(e) => setTimeRange(Number(e.target.value))}
            className="px-4 py-2 bg-card border border-border rounded-lg text-sm font-medium text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
          >
            <option value={1}>Last 24 Hours</option>
            <option value={7}>Last 7 Days</option>
            <option value={30}>Last 30 Days</option>
            <option value={90}>Last 90 Days</option>
          </select>
        </div>

        <div className="flex items-center gap-3">
          <button
            onClick={loadMetrics}
            className="flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg text-sm font-medium hover:bg-primary/90 transition-colors"
          >
            <Icon name="ArrowPathIcon" size={16} />
            Refresh
          </button>
          <button
            onClick={handleClearData}
            className="flex items-center gap-2 px-4 py-2 bg-destructive/10 text-destructive rounded-lg text-sm font-medium hover:bg-destructive/20 transition-colors"
          >
            <Icon name="TrashIcon" size={16} />
            Clear Data
          </button>
        </div>
      </div>

      {/* Metrics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <MetricsCard
          title="Page Views"
          value={metrics.totalPageViews}
          icon="EyeIcon"
          color="primary"
        />
        <MetricsCard
          title="Shipment Searches"
          value={metrics.totalShipmentSearches}
          icon="MagnifyingGlassIcon"
          color="secondary"
        />
        <MetricsCard
          title="Quote Requests"
          value={metrics.totalQuoteRequests}
          icon="DocumentTextIcon"
          color="accent"
        />
        <MetricsCard
          title="Conversions"
          value={metrics.totalConversions}
          icon="CheckCircleIcon"
          color="success"
        />
      </div>

      {/* Secondary Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <div className="bg-card rounded-xl p-6 shadow-card border border-border">
          <div className="flex items-center gap-3 mb-2">
            <Icon name="UsersIcon" size={20} className="text-primary" />
            <h3 className="text-sm font-medium text-muted-foreground">Unique Visitors</h3>
          </div>
          <p className="text-3xl font-bold text-foreground">{metrics.uniqueVisitors}</p>
        </div>

        <div className="bg-card rounded-xl p-6 shadow-card border border-border">
          <div className="flex items-center gap-3 mb-2">
            <Icon name="ClockIcon" size={20} className="text-secondary" />
            <h3 className="text-sm font-medium text-muted-foreground">Avg. Session Duration</h3>
          </div>
          <p className="text-3xl font-bold text-foreground">{formatDuration(metrics.averageSessionDuration)}</p>
        </div>

        <div className="bg-card rounded-xl p-6 shadow-card border border-border">
          <div className="flex items-center gap-3 mb-2">
            <Icon name="ChartBarIcon" size={20} className="text-accent" />
            <h3 className="text-sm font-medium text-muted-foreground">Conversion Rate</h3>
          </div>
          <p className="text-3xl font-bold text-foreground">{metrics.conversionRate.toFixed(2)}%</p>
        </div>
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        <ChartCard
          title="Top Pages"
          data={metrics.topPages}
          dataKey="views"
          nameKey="page"
          color="#3b82f6"
        />
        <ChartCard
          title="Top Search Routes"
          data={metrics.topSearchRoutes}
          dataKey="count"
          nameKey="route"
          color="#10b981"
        />
      </div>

      {/* Events Table */}
      <EventsTable />
    </div>
  );
}